<template>
  <div class="dashboard-container">
    <!-- HEADER SECTION -->
    <b-card class="mb-4 dashboard-header" no-body>
      <b-card-body class="d-flex justify-content-between align-items-center py-3">
        <div>
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper mr-3">
              <b-icon-arrow-left-right class="header-icon" />
            </div>
            <div>
              <h5 class="mb-0 header-title">SALE VS REFUNDS ANALYTICS</h5>
              <small class="header-subtitle">Track sales performance and refund trends across all locations</small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="light" class="mr-2 date-badge">
            <b-icon-calendar class="mr-1" />{{ formattedDateRange }}
          </b-badge>
          <b-badge variant="primary" pill class="animated-badge" :class="getRefundRateColorClass(refundRate)">
            {{ refundRate.toFixed(1) }}% Refund Rate
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <!-- FILTERS ROW -->
    <b-card class="mb-4 filters-card" no-body>
      <b-card-body class="py-3">
        <div class="row align-items-end">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-end" style="gap: 16px;">
              <div style="min-width: 320px;">
                <label class="small text-muted mb-2 filter-label">
                  <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                </label>
                <date-picker
                  v-model="date_range"
                  format="MM-DD-YYYY"
                  valueType="format"
                  class="custom-datepicker"
                  placeholder="SELECT DATE RANGE"
                  range
                  :class="{ 'datepicker-focused': dateRangeFocused }"
                  @focus="dateRangeFocused = true"
                  @blur="dateRangeFocused = false"
                ></date-picker>
              </div>

              <div class="d-flex flex-wrap" style="gap: 8px;">
                <b-button
                  v-for="btn in quickDateButtons"
                  :key="btn.id"
                  size="md"
                  :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                  @click="set_dates(btn.id)"
                  class="quick-date-btn py-2 px-3"
                >
                  <b-icon v-if="picked_date_range === btn.id" :icon="btn.icon" class="mr-1" size="sm" />
                  {{ btn.label }}
                </b-button>
              </div>
            </div>
          </div>

          <div class="col-lg-4 mt-3 mt-lg-0">
            <div class="d-flex justify-content-end">
              <b-button
                variant="success"
                @click="fetch_init"
                class="search-btn"
                :disabled="items_being_loaded"
              >
                <b-icon-search class="mr-2" />
                <span v-if="!items_being_loaded">ANALYZE</span>
                <span v-else>
                  <b-icon-arrow-repeat spin class="mr-2" />LOADING...
                </span>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ADVANCED FILTERS -->
        <div class="mt-3">
          <b-button
            variant="link"
            size="md"
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="p-0 text-decoration-none advanced-toggle"
          >
            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
          </b-button>

          <b-collapse v-model="showAdvancedFilters">
            <div class="mt-3 pt-3 border-top">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-building class="mr-1" size="sm" />LOCATION
                  </label>
                  <b-form-select
                    :options="locationOptions"
                    v-model="selected_location_id"
                    size="md"
                    class="filter-select"
                    @change="onLocationChange"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null">ALL LOCATIONS</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-graph-up class="mr-1" size="sm" />CHART VIEW
                  </label>
                  <b-form-radio-group
                    v-model="chartView"
                    size="md"
                    buttons
                    button-variant="outline-secondary"
                  >
                    <b-form-radio value="sales">Sales</b-form-radio>
                    <b-form-radio value="refunds">Refunds</b-form-radio>
                    <b-form-radio value="net">Net</b-form-radio>
                  </b-form-radio-group>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>

        <!-- ACTIVE FILTERS DISPLAY -->
        <div class="mt-3 pt-3 border-top" v-if="selected_location_id">
          <div class="d-flex flex-wrap align-items-center" style="gap: 12px;">
            <span class="small text-muted" style="font-size: 14px;">Active Filter:</span>
            <b-badge variant="primary" pill class="active-filter-badge">
              <b-icon-building class="mr-1" size="xs" />{{ getLocationName(selected_location_id) }}
              <b-icon-x class="ml-1" size="xs" @click="clearLocationFilter" style="cursor: pointer;" />
            </b-badge>
            <span class="small text-muted ml-2" style="font-size: 14px;">Showing data for selected location only</span>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- LOADING STATE -->
    <div v-if="items_being_loaded" class="text-center py-5">
      <b-spinner variant="primary" type="grow" label="Loading..." style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 text-muted" style="font-size: 18px;">Fetching sales data...</p>
    </div>

    <!-- MAIN CONTENT -->
    <template v-else-if="items.length > 0">
      <!-- ============================================== -->
      <!-- KPI CARDS - REFERENCE STYLE (OPTION A)         -->
      <!-- ============================================== -->
      <div class="kpi-grid">
        <!-- Card 1: Total Sales (DARK) -->
        <div class="kpi-card dark accent-green">
          <div class="kpi-icon-wrap icon-green">
            <b-icon-cash-stack />
          </div>
          <span class="kpi-label">Total Sales</span>
          <span class="kpi-value">{{ formatCurrency(totalSales) }}</span>
          <div class="kpi-trend trend-green">
            <b-icon-building />
            {{ filteredItems.length }} locations
          </div>
        </div>

        <!-- Card 2: Total Refunds -->
        <div class="kpi-card accent-red">
          <div class="kpi-icon-wrap icon-red">
            <b-icon-arrow-return-left />
          </div>
          <span class="kpi-label">Total Refunds</span>
          <span class="kpi-value">{{ formatCurrency(totalRefunds) }}</span>
          <div class="kpi-trend" :class="refundRate < 10 ? 'trend-green' : 'trend-red'">
            <b-icon-graph-down-arrow v-if="refundRate >= 10" />
            <b-icon-graph-up-arrow v-else />
            {{ refundRate.toFixed(1) }}% rate
          </div>
        </div>

        <!-- Card 3: Net Revenue -->
        <div class="kpi-card accent-purple">
          <div class="kpi-icon-wrap icon-purple">
            <b-icon-wallet />
          </div>
          <span class="kpi-label">Net Revenue</span>
          <span class="kpi-value">{{ formatCurrency(netRevenue) }}</span>
          <div class="kpi-trend trend-purple">
            <b-icon-percent />
            {{ netMargin.toFixed(1) }}% margin
          </div>
        </div>

        <!-- Card 4: Avg Refund Rate -->
        <div class="kpi-card accent-amber">
          <div class="kpi-icon-wrap icon-amber">
            <b-icon-percent />
          </div>
          <span class="kpi-label">Avg Refund Rate</span>
          <span class="kpi-value">{{ avgRefundRate.toFixed(1) }}%</span>
          <div class="kpi-trend trend-amber">
            <b-icon-building />
            per location
          </div>
        </div>
      </div>

      <!-- CHARTS SECTION -->
      <div class="row mb-3">
        <div class="col-lg-8 mb-3">
          <b-card no-body class="chart-card">
            <b-card-header class="bg-white border-bottom py-2">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="chart-header-icon mr-2">
                    <b-icon-bar-chart-line />
                  </div>
                  <h6 class="mb-0 chart-title">Sales vs Refunds by Location</h6>
                </div>
                <div class="chart-view-buttons">
                  <b-button-group size="md">
                    <b-button
                      :variant="chartView === 'sales' ? 'success' : 'outline-secondary'"
                      @click="chartView = 'sales'"
                    >
                      <b-icon-graph-up class="mr-1" size="sm" />Sales
                    </b-button>
                    <b-button
                      :variant="chartView === 'refunds' ? 'danger' : 'outline-secondary'"
                      @click="chartView = 'refunds'"
                    >
                      <b-icon-arrow-return-left class="mr-1" size="sm" />Refunds
                    </b-button>
                    <b-button
                      :variant="chartView === 'net' ? 'primary' : 'outline-secondary'"
                      @click="chartView = 'net'"
                    >
                      <b-icon-cash-stack class="mr-1" size="sm" />Net
                    </b-button>
                  </b-button-group>
                </div>
              </div>
            </b-card-header>
            <b-card-body class="p-3">
              <div class="chart-container" style="height: 340px;">
                <canvas ref="salesVsRefundsChart"></canvas>
              </div>
            </b-card-body>
          </b-card>
        </div>
        <div class="col-lg-4 mb-3">
          <b-card no-body class="chart-card">
            <b-card-header class="bg-white border-bottom py-2">
              <div class="d-flex align-items-center">
                <div class="chart-header-icon mr-2 bg-danger">
                  <b-icon-exclamation-triangle />
                </div>
                <h6 class="mb-0 chart-title">Refund Rate by Location</h6>
              </div>
            </b-card-header>
            <b-card-body class="p-3 d-flex justify-content-center">
              <div class="pie-chart-container" style="height: 320px;">
                <canvas ref="refundRateChart"></canvas>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </div>

      <!-- PERFORMANCE INSIGHTS -->
      <div class="row mb-3">
        <div class="col-lg-6 mb-3">
          <b-card no-body class="insight-card h-100">
            <b-card-header class="bg-white border-bottom py-2">
              <div class="d-flex align-items-center">
                <div class="insight-icon bg-success-light mr-2">
                  <b-icon-trophy />
                </div>
                <h6 class="mb-0 chart-title">Top Performers</h6>
                <span class="ml-2 small text-muted" style="font-size: 14px;">Highest Net Revenue</span>
              </div>
            </b-card-header>
            <b-card-body class="p-0">
              <div class="insight-list">
                <div v-for="(item, index) in topPerformers" :key="index" class="insight-item">
                  <div class="insight-rank" :class="getRankClass(index)">
                    {{ index + 1 }}
                  </div>
                  <div class="insight-content">
                    <div class="d-flex justify-content-between align-items-center w-100">
                      <div>
                        <h6 class="mb-0 insight-location">{{ getLocationName(item.locsub) }}</h6>
                        <small class="text-muted insight-meta">
                          <b-icon-percent class="mr-1" size="xs" />{{ (item.total_refunds / item.total_sale * 100).toFixed(1) }}% refund rate
                        </small>
                      </div>
                      <div class="text-right">
                        <span class="net-revenue positive">+{{ formatCurrency(item.delta) }}</span>
                        <small class="d-block text-muted insight-meta">{{ formatCurrency(item.total_sale) }} sales</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="topPerformers.length === 0" class="p-4 text-center text-muted">
                  <b-icon-emoji-frown class="mb-2" font-scale="2" />
                  <p class="mb-0" style="font-size: 16px;">No performance data available</p>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </div>

        <div class="col-lg-6 mb-3">
          <b-card no-body class="insight-card h-100">
            <b-card-header class="bg-white border-bottom py-2">
              <div class="d-flex align-items-center">
                <div class="insight-icon bg-warning-light mr-2">
                  <b-icon-exclamation-triangle />
                </div>
                <h6 class="mb-0 chart-title">Refund Watchlist</h6>
                <span class="ml-2 small text-muted" style="font-size: 14px;">Highest Refund Rates</span>
              </div>
            </b-card-header>
            <b-card-body class="p-0">
              <div class="insight-list">
                <div v-for="(item, index) in refundWatchlist" :key="index" class="insight-item">
                  <div class="insight-rank-warning" :class="getRefundRateClass(item)">
                    {{ (item.total_refunds / item.total_sale * 100).toFixed(0) }}%
                  </div>
                  <div class="insight-content">
                    <div class="d-flex justify-content-between align-items-center w-100">
                      <div>
                        <h6 class="mb-0 insight-location">{{ getLocationName(item.locsub) }}</h6>
                        <small class="text-muted insight-meta">
                          <b-icon-cash class="mr-1" size="xs" />{{ formatCurrency(item.total_sale) }} total
                        </small>
                      </div>
                      <div class="text-right">
                        <span class="refund-amount negative">-{{ formatCurrency(item.total_refunds) }}</span>
                        <small class="d-block text-muted insight-meta">{{ ((item.total_refunds / item.total_sale * 100)).toFixed(1) }}% rate</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="refundWatchlist.length === 0" class="p-4 text-center text-muted">
                  <b-icon-check-circle class="mb-2" font-scale="2" variant="success" />
                  <p class="mb-0" style="font-size: 16px;">Great! No high refund rates detected</p>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </div>

      <!-- LOCATION PERFORMANCE TABLE -->
      <b-card no-body class="mt-3 data-table-card">
        <b-card-header class="bg-light d-flex justify-content-between align-items-center py-2">
          <div class="d-flex align-items-center">
            <b-icon-building class="mr-2" size="sm" />
            <h6 class="mb-0 table-title">Location Performance Details</h6>
            <b-badge variant="secondary" class="ml-2 table-badge">{{ filteredItems.length }} locations</b-badge>
            <b-badge variant="light" class="ml-2 table-badge" v-if="!selected_location_id">
              <b-icon-mouse2 size="sm" class="mr-1" />Click row to filter
            </b-badge>
            <b-badge variant="info" class="ml-2 table-badge" v-else>
              <b-icon-check-circle size="sm" class="mr-1" />Filtered by location
            </b-badge>
          </div>
          <div>
            <b-button size="md" variant="outline-secondary" @click="exportToCSV" class="mr-2">
              <b-icon-download class="mr-1" size="sm" /> Export
            </b-button>
            <b-button size="md" variant="outline-primary" @click="fetch_init">
              <b-icon-arrow-repeat class="mr-1" size="sm" /> Refresh
            </b-button>
          </div>
        </b-card-header>
        <b-card-body class="p-0">
          <div class="table-wrapper-sm" style="max-height: 400px;">
            <table class="compact-table">
              <thead>
                <tr>
                  <th @click="sortby('locsub')">LOCATION</th>
                  <th class="text-right" @click="sortby('total_sale')">SALES ($)</th>
                  <th class="text-right" @click="sortby('total_refunds')">REFUNDS ($)</th>
                  <th class="text-right" @click="sortby('refund_rate')">REFUND RATE</th>
                  <th class="text-right" @click="sortby('delta')">NET ($)</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in paginatedItems"
                  :key="item.locsub"
                  :class="{ 'highlight-row': selected_location_id == item.locsub }"
                  @click="selectLocationFromTable(item.locsub)"
                >
                  <td>{{ getLocationName(item.locsub) }}</td>
                  <td class="text-right sale-amount">{{ formatCurrency(item.total_sale || 0) }}</td>
                  <td class="text-right refund-amount">{{ formatCurrency(item.total_refunds || 0) }}</td>
                  <td class="text-right">
                    <b-badge :variant="getRefundRateVariant(item)" pill class="refund-badge">
                      {{ ((item.total_refunds / item.total_sale) * 100 || 0).toFixed(1) }}%
                    </b-badge>
                  </td>
                  <td class="text-right" :class="getDeltaClass(item)">{{ formatCurrency(item.delta || 0) }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-light font-weight-bold">
                <tr>
                  <td>TOTAL</td>
                  <td class="text-right">{{ formatCurrency(totalSales) }}</td>
                  <td class="text-right text-danger">{{ formatCurrency(totalRefunds) }}</td>
                  <td class="text-right">{{ refundRate.toFixed(1) }}%</td>
                  <td class="text-right text-success">{{ formatCurrency(netRevenue) }}</td>
                </tr>
              </tfoot>
            </table>

            <!-- Pagination -->
            <div class="pagination-wrapper d-flex justify-content-between align-items-center p-3 bg-light">
              <small class="text-muted" style="font-size: 16px;">
                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredItems.length) }} of {{ filteredItems.length }} locations
              </small>
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredItems.length"
                :per-page="pageSize"
                size="md"
                class="mb-0"
                align="center"
              ></b-pagination>
              <b-form-select v-model="pageSize" size="md" style="width: 85px;" @change="currentPage = 1">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
              </b-form-select>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </template>

    <!-- NO DATA STATE -->
    <div v-else-if="!items_being_loaded && items.length === 0" class="text-center py-5">
      <b-alert show variant="light" class="mb-0" style="font-size: 18px;">
        <b-icon-search class="mr-2" />
        Select a date range and click ANALYZE to view sales vs refunds data
      </b-alert>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import Chart from 'chart.js';
import moment from 'moment';
import 'moment/locale/en-gb';
import global_vars from '@/utilities/global_vars';
import Backer from '@/components/globals/Backer';
import Requestor from '@/utilities/Requestor';
import {
  BIconSearch,
  BIconGraphUp,
  BIconArrowReturnLeft,
  BIconCashStack,
  BIconExclamationTriangle,
  BIconCheckCircle,
  BIconDashCircle,
  BIconCalendar,
  BIconBuilding,
  BIconTable,
  BIconDownload,
  BIconArrowRepeat,
  BIconArrowUpShort,
  BIconArrowDownShort,
  BIconTrophy,
  BIconWallet,
  BIconPercent,
  BIconCash,
  BIconX,
  BIconBarChartLine,
  BIconEmojiFrown,
  BIconArrowLeftRight,
  BIconInfoCircle,
  BIconEyeSlash,
  BIconMouse2,
  BIconPeople,
  BIconTag,
  BIconPerson,
  BIconCartPlus,
  BIconClockHistory,
  BIconPieChart,
  BIconFilterCircle
} from 'bootstrap-vue';

moment.locale('en-gb');

export default {
  name: "ReportsRefunds",
  components: {
    Backer,
    DatePicker,
    BIconSearch,
    BIconGraphUp,
    BIconArrowReturnLeft,
    BIconCashStack,
    BIconExclamationTriangle,
    BIconCheckCircle,
    BIconDashCircle,
    BIconCalendar,
    BIconBuilding,
    BIconTable,
    BIconDownload,
    BIconArrowRepeat,
    BIconArrowUpShort,
    BIconArrowDownShort,
    BIconTrophy,
    BIconWallet,
    BIconPercent,
    BIconCash,
    BIconX,
    BIconBarChartLine,
    BIconEmojiFrown,
    BIconArrowLeftRight,
    BIconInfoCircle,
    BIconEyeSlash,
    BIconMouse2,
    BIconPeople,
    BIconTag,
    BIconPerson,
    BIconCartPlus,
    BIconClockHistory,
    BIconPieChart,
    BIconFilterCircle
  },
  data: function(){
    return {
      packet: {},
      items: [],
      items_being_loaded: false,
      global_vars: global_vars,
      date_range: [],
      picked_date_range: null,
      sortKey: null,
      sortAsc: false,
      chartView: 'sales',
      dateRangeFocused: false,
      showAdvancedFilters: false,
      currentPage: 1,
      pageSize: 10,

      // Location filter
      selected_location_id: null,
      user_allowed_location_ids: [],
      user_multiple_locations_enabled: false,

      // Chart instances
      salesVsRefundsChart: null,
      refundRateChart: null,

      // Quick date buttons
      quickDateButtons: [
        { id: 'today', label: 'Today', icon: 'calendar-day' },
        { id: 'yesterday', label: 'Yesterday', icon: 'calendar-day' },
        { id: 'this_week', label: 'This Week', icon: 'calendar-week' },
        { id: 'last_week', label: 'Last Week', icon: 'calendar-week' },
        { id: 'this_month', label: 'This Month', icon: 'calendar-month' },
        { id: 'last_month', label: 'Last Month', icon: 'calendar-month' }
      ],

      chartColors: {
        success: '#10b981',
        danger: '#ef4444',
        primary: '#6366f1',
        warning: '#f59e0b',
        info: '#06b6d4'
      }
    }
  },
  computed: {
    filteredItems() {
      if (this.selected_location_id) {
        return this.items.filter(item => item.locsub == this.selected_location_id);
      }
      return this.items;
    },
    totalSales() {
      return this.filteredItems.reduce((sum, item) => sum + (item.total_sale || 0), 0);
    },
    totalRefunds() {
      return this.filteredItems.reduce((sum, item) => sum + (item.total_refunds || 0), 0);
    },
    netRevenue() {
      return this.totalSales - this.totalRefunds;
    },
    refundRate() {
      return this.totalSales > 0 ? (this.totalRefunds / this.totalSales * 100) : 0;
    },
    avgRefundRate() {
      if (this.filteredItems.length === 0) return 0;
      const total = this.filteredItems.reduce((sum, item) => sum + (item.total_sale > 0 ? (item.total_refunds / item.total_sale * 100) : 0), 0);
      return total / this.filteredItems.length;
    },
    netMargin() {
      return this.totalSales > 0 ? (this.netRevenue / this.totalSales * 100) : 0;
    },
    topPerformers() {
      return [...this.filteredItems]
        .filter(item => item.delta > 0)
        .sort((a, b) => b.delta - a.delta)
        .slice(0, 5);
    },
    refundWatchlist() {
      return [...this.filteredItems]
        .filter(item => item.total_sale > 0 && item.total_refunds > 0)
        .sort((a, b) => (b.total_refunds / b.total_sale) - (a.total_refunds / a.total_sale))
        .slice(0, 5);
    },
    showLocationFilter() {
      const userrole = this.packet.userDetails.userrole;
      return userrole === 'admin' || userrole === 'accountmanager' ||
             (userrole === 'locationadmin' && this.user_multiple_locations_enabled);
    },
    locationOptions() {
      let options = [{ value: null, text: 'ALL LOCATIONS' }];

      if (this.user_multiple_locations_enabled && this.user_allowed_location_ids.length > 0) {
        this.user_allowed_location_ids.forEach(locId => {
          const location = this.packet.locations.find(l => l.value === locId);
          if (location) {
            options.push({ value: location.value, text: location.text });
          }
        });
      } else if (this.packet.locations && this.packet.userDetails.userrole === 'admin') {
        options.push(...this.packet.locations);
      }

      return options;
    },
    formattedDateRange() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} - ${this.date_range[1]}`;
      }
      return 'Select range';
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredItems.slice(start, end);
    }
  },
  watch: {
    filteredItems() {
      this.currentPage = 1;
      this.updateCharts();
    },
    chartView() {
      this.updateCharts();
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.loadUserLocationPermissions();
    this.initializeDateRange();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.salesVsRefundsChart) this.salesVsRefundsChart.destroy();
    if (this.refundRateChart) this.refundRateChart.destroy();
  },
  methods: {
    formatCurrency(value) {
      if (value === undefined || value === null) return '$0.00';
      return '$' + parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    initializeDateRange() {
      const today = moment();
      this.date_range = [
        today.startOf('month').format('MM-DD-YYYY'),
        today.endOf('month').format('MM-DD-YYYY')
      ];
      this.picked_date_range = 'this_month';
      this.fetch_init();
    },

    loadUserLocationPermissions() {
      const userrole = this.packet.userDetails.userrole;
      const properties = this.packet.userDetails.properties || {};

      this.user_multiple_locations_enabled = properties.multiple_locations_enabled === true;
      this.user_allowed_location_ids = properties.location_ids || [];
    },

    getLocationName(locsub) {
      if (!locsub) return 'Unknown';
      if (typeof locsub === 'number') {
        const location = this.packet.locations.find(l => l.value === locsub);
        return location ? location.text : locsub.toString();
      }
      return this.packet.locsubsdict[locsub] || locsub;
    },

    getRefundRate(item) {
      return item.total_sale > 0 ? (item.total_refunds / item.total_sale * 100) : 0;
    },

    getRefundRateVariant(item) {
      const rate = this.getRefundRate(item);
      if (rate > 20) return 'danger';
      if (rate > 10) return 'warning';
      return 'success';
    },

    getRefundRateColorClass(rate) {
      if (rate > 20) return 'badge-danger-glow';
      if (rate > 10) return 'badge-warning-glow';
      return 'badge-success-glow';
    },

    getRefundRateProgressClass(item) {
      const rate = this.getRefundRate(item);
      if (rate > 20) return 'bg-danger';
      if (rate > 10) return 'bg-warning';
      return 'bg-success';
    },

    getDeltaClass(item) {
      if (item.delta < 0) return 'negative-value';
      if (item.delta > 0) return 'positive-value';
      return '';
    },

    getRankClass(index) {
      if (index === 0) return 'rank-gold';
      if (index === 1) return 'rank-silver';
      if (index === 2) return 'rank-bronze';
      return '';
    },

    getRefundRateClass(item) {
      const rate = this.getRefundRate(item);
      if (rate > 20) return 'high-risk';
      if (rate > 10) return 'medium-risk';
      return 'low-risk';
    },

    calculateAvgSalesPerLocation() {
      if (this.filteredItems.length === 0) return 0;
      const avg = (this.totalSales / this.filteredItems.length) / (this.totalSales / 100);
      return Math.round(avg * 100) / 100;
    },

    set_dates: function(value){
      let start = null;
      let end = null;

      switch(value) {
        case 'today':
          start = moment().format("MM-DD-YYYY");
          end = moment().format("MM-DD-YYYY");
          break;
        case 'yesterday':
          start = moment().add(-1, 'days').format("MM-DD-YYYY");
          end = moment().add(-1, 'days').format("MM-DD-YYYY");
          break;
        case 'this_week':
          start = moment().startOf('week').format("MM-DD-YYYY");
          end = moment().endOf('week').format("MM-DD-YYYY");
          break;
        case 'last_week':
          start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY");
          end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY");
          break;
        case 'this_month':
          start = moment().startOf('month').format("MM-DD-YYYY");
          end = moment().endOf('month').format("MM-DD-YYYY");
          break;
        case 'last_month':
          start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY");
          end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY");
          break;
      }

      if (start && end) {
        this.date_range = [start , end];
        this.picked_date_range = value;
        this.fetch_init();
      }
    },

    onLocationChange() {
      this.currentPage = 1;
      this.fetch_init();
    },

    clearLocationFilter() {
      this.selected_location_id = null;
      this.currentPage = 1;
      this.fetch_init();
    },

    selectLocationFromTable(locationId) {
      if (this.selected_location_id === locationId) {
        this.selected_location_id = null;
      } else {
        this.selected_location_id = locationId;
      }
      this.currentPage = 1;
    },

    fetch_init: function(){
      let dates = this.date_range;
      let that = this;
      let data = {dates: dates, username: this.packet.userDetails.username}

      if (this.selected_location_id) {
        data.location_id = this.selected_location_id;
      }

      let path = "/api/reports/reports_v4/"
      let s = function(response) {
        if (response.data.result){
          that.items = response.data.result.map(item => ({
            ...item,
            refund_rate: item.total_sale > 0 ? (item.total_refunds / item.total_sale * 100) : 0
          }));
        } else {
          that.$bvToast.toast(
            response.data.error || "No data returned",
            {title: "Error", autoHideDelay: 1500, variant:"warning"}
          );
        }
        that.items_being_loaded = false
      }

      let f = function(error) {
        console.log(error)
        that.$bvToast.toast(
          "Error occurred. Please try again.",
          {title: "Error", autoHideDelay: 1500, variant:"danger"}
        );
        that.items_being_loaded = false
      }
      this.items_being_loaded = true;
      Requestor.postSyncP(path, data, this, s, f);
    },

    sortby: function(key){
      this.sortKey = key;

      if (key === 'refund_rate') {
        this.items.sort((a, b) => {
          const rateA = a.total_sale > 0 ? (a.total_refunds / a.total_sale * 100) : 0;
          const rateB = b.total_sale > 0 ? (b.total_refunds / b.total_sale * 100) : 0;
          return this.sortAsc ? rateA - rateB : rateB - rateA;
        });
      } else {
        if (this.sortAsc){
          this.items = Requestor.sort(this.items, key)
        } else {
          this.items = Requestor.sort_reverse(this.items, key)
        }
      }
      this.sortAsc = !this.sortAsc;
      this.currentPage = 1;
    },

    exportToCSV() {
      const headers = ['Location', 'Total Sales', 'Total Refunds', 'Refund Rate (%)', 'Net Revenue'];
      const rows = this.filteredItems.map(item => [
        this.getLocationName(item.locsub),
        item.total_sale || 0,
        item.total_refunds || 0,
        ((item.total_refunds / item.total_sale) * 100 || 0).toFixed(1),
        item.delta || 0
      ]);

      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_vs_refunds_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      this.$bvToast.toast('Export completed!', { title: 'Success', autoHideDelay: 1500, variant: 'success' });
    },

    handleResize() {
      this.updateCharts();
    },

    updateCharts() {
      this.$nextTick(() => {
        this.renderSalesVsRefundsChart();
        this.renderRefundRateChart();
      });
    },

    renderSalesVsRefundsChart() {
      const ctx = this.$refs.salesVsRefundsChart;
      if (!ctx || this.filteredItems.length === 0) return;

      if (this.salesVsRefundsChart) {
        this.salesVsRefundsChart.destroy();
      }

      const sortedItems = [...this.filteredItems].sort((a, b) => {
        switch(this.chartView) {
          case 'sales': return b.total_sale - a.total_sale;
          case 'refunds': return b.total_refunds - a.total_refunds;
          case 'net': return b.delta - a.delta;
          default: return b.total_sale - a.total_sale;
        }
      });

      const labels = sortedItems.map(item => {
        const name = this.getLocationName(item.locsub);
        return name.length > 15 ? name.substring(0, 12) + '...' : name;
      });
      const salesData = sortedItems.map(item => item.total_sale || 0);
      const refundsData = sortedItems.map(item => item.total_refunds || 0);
      const netData = sortedItems.map(item => item.delta || 0);

      let datasets = [];

      switch(this.chartView) {
        case 'sales':
          datasets = [
            {
              label: 'Sales',
              data: salesData,
              backgroundColor: 'rgba(16, 185, 129, 0.8)',
              borderColor: '#10b981',
              borderWidth: 1,
              borderRadius: 6,
              barPercentage: 0.6
            },
            {
              label: 'Refunds',
              data: refundsData,
              backgroundColor: 'rgba(239, 68, 68, 0.8)',
              borderColor: '#ef4444',
              borderWidth: 1,
              borderRadius: 6,
              barPercentage: 0.6
            }
          ];
          break;
        case 'refunds':
          datasets = [
            {
              label: 'Refunds',
              data: refundsData,
              backgroundColor: 'rgba(239, 68, 68, 0.8)',
              borderColor: '#ef4444',
              borderWidth: 1,
              borderRadius: 6,
              barPercentage: 0.6
            },
            {
              label: 'Sales',
              data: salesData,
              backgroundColor: 'rgba(16, 185, 129, 0.5)',
              borderColor: '#10b981',
              borderWidth: 1,
              borderRadius: 6,
              barPercentage: 0.6
            }
          ];
          break;
        case 'net':
          datasets = [
            {
              label: 'Net Revenue',
              data: netData,
              backgroundColor: netData.map(value => value >= 0 ? 'rgba(99, 102, 241, 0.8)' : 'rgba(239, 68, 68, 0.8)'),
              borderColor: netData.map(value => value >= 0 ? '#6366f1' : '#ef4444'),
              borderWidth: 1,
              borderRadius: 6,
              barPercentage: 0.6
            }
          ];
          break;
      }

      this.salesVsRefundsChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: { usePointStyle: true, boxWidth: 12, font: { size: 14 } }
            },
            tooltip: {
              bodyFont: { size: 16 },
              titleFont: { size: 16 },
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) label += ': ';
                  label += '$' + context.parsed.y.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });
                  return label;
                }
              }
            }
          },
          scales: {
            x: {
              ticks: { maxRotation: 45, minRotation: 45, font: { size: 14 } },
              grid: { display: false }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0, 0, 0, 0.05)' },
              ticks: {
                font: { size: 14 },
                callback: function(value) {
                  return '$' + value.toLocaleString();
                }
              }
            }
          }
        }
      });
    },

    renderRefundRateChart() {
      const ctx = this.$refs.refundRateChart;
      if (!ctx || this.filteredItems.length === 0) return;

      if (this.refundRateChart) {
        this.refundRateChart.destroy();
      }

      const validItems = this.filteredItems.filter(item => item.total_sale > 0);
      const sortedItems = [...validItems]
        .sort((a, b) => (b.total_refunds / b.total_sale) - (a.total_refunds / a.total_sale))
        .slice(0, 10);

      const labels = sortedItems.map(item => {
        const name = this.getLocationName(item.locsub);
        return name.length > 12 ? name.substring(0, 10) + '...' : name;
      });
      const refundRates = sortedItems.map(item => (item.total_refunds / item.total_sale * 100));

      const backgroundColors = refundRates.map(rate => {
        if (rate > 20) return '#ef4444';
        if (rate > 10) return '#f59e0b';
        return '#10b981';
      });

      this.refundRateChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: refundRates,
            backgroundColor: backgroundColors,
            borderWidth: 2,
            borderColor: '#fff',
            hoverOffset: 8
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'right',
              labels: { usePointStyle: true, boxWidth: 10, font: { size: 13 } }
            },
            tooltip: {
              bodyFont: { size: 16 },
              titleFont: { size: 16 },
              callbacks: {
                label: function(context) {
                  const item = sortedItems[context.dataIndex];
                  return [
                    `${context.label}: ${context.raw.toFixed(1)}% refund rate`,
                    `Sales: $${item.total_sale.toLocaleString()}`,
                    `Refunds: $${item.total_refunds.toLocaleString()}`,
                    `Net: $${item.delta.toLocaleString()}`
                  ];
                }
              }
            }
          }
        }
      });
    }
  },
  created: function(){
    const storedPacket = localStorage.getItem('packet');
    const storedUserDetails = localStorage.getItem('userDetails');

    this.packet = storedPacket ? JSON.parse(storedPacket) : {};
    this.packet.userDetails = storedUserDetails ? JSON.parse(storedUserDetails) : {};

    if (!this.packet.locsubsdict) {
      this.packet.locsubsdict = {};
    }
    if (!this.packet.locations) {
      this.packet.locations = [];
    }
  }
}
</script>

<style scoped>
/* =========================================================
   GLOBAL
   ========================================================= */
.dashboard-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 24px;
}

/* =========================================================
   HEADER
   ========================================================= */
.dashboard-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  border: none;
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
}

.header-icon-wrapper {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon {
  font-size: 28px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.header-title {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.header-subtitle {
  font-size: 16px;
  opacity: 0.8;
}

.date-badge {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.animated-badge {
  animation: fadeIn 0.5s ease-out;
  font-size: 14px;
  padding: 8px 16px;
}

.badge-danger-glow {
  background: #ef4444;
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.5);
}

.badge-warning-glow {
  background: #f59e0b;
  box-shadow: 0 0 12px rgba(245, 158, 11, 0.5);
}

.badge-success-glow {
  background: #10b981;
  box-shadow: 0 0 12px rgba(16, 185, 129, 0.5);
}

/* =========================================================
   FILTERS
   ========================================================= */
.filters-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background: white;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 18px;
  width: 100%;
}

.datepicker-focused {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
}

.quick-date-btn {
  border-radius: 20px;
  font-size: 16px;
  padding: 10px 18px;
  font-weight: 500;
}

.filter-select {
  border-radius: 12px;
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 18px;
  padding: 10px 16px;
}

.search-btn {
  border-radius: 12px;
  padding: 12px 36px;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.5px;
}

.advanced-toggle {
  font-size: 16px;
  font-weight: 500;
}

.active-filter-badge {
  cursor: default;
  background: #6366f1;
  color: #fff;
  font-size: 14px;
  padding: 8px 16px;
}

/* =========================================================
   KPI CARDS - REFERENCE STYLE (OPTION A)
   ========================================================= */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.kpi-card {
  background: #fff;
  border-radius: 20px;
  padding: 36px 24px 30px;
  border: 1px solid #e8edf3;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  position: relative;
  overflow: hidden;
}

.kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

.kpi-card:hover {
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.kpi-card.dark {
  background: #0b1220;
  border-color: #0b1220;
  box-shadow: 0 8px 26px rgba(11, 18, 32, 0.35);
}

.kpi-card.dark .kpi-label {
  color: rgba(255, 255, 255, 0.65);
}

.kpi-card.dark .kpi-value {
  color: #ffffff;
}

/* Accent stripe colors */
.accent-green::before   { background: linear-gradient(90deg, #10b981, #34d399); }
.accent-red::before     { background: linear-gradient(90deg, #ef4444, #f87171); }
.accent-purple::before  { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.accent-amber::before   { background: linear-gradient(90deg, #f59e0b, #fbbf24); }

/* Icon box */
.kpi-icon-wrap {
  width: 76px;
  height: 76px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 34px;
  margin-bottom: 22px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.icon-green   { background: linear-gradient(135deg, #10b981, #34d399); }
.icon-red     { background: linear-gradient(135deg, #ef4444, #f87171); }
.icon-purple  { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.icon-amber   { background: linear-gradient(135deg, #f59e0b, #fbbf24); }

/* Label */
.kpi-label {
  font-size: 18px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 12px;
  letter-spacing: 0.2px;
}

/* Big value */
.kpi-value {
  font-size: 46px;
  font-weight: 800;
  color: #0b1220;
  line-height: 1.1;
  letter-spacing: -1.5px;
  margin-bottom: 18px;
  word-break: break-word;
}

/* Trend pill badge */
.kpi-trend {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 999px;
  white-space: nowrap;
}

.trend-green   { color: #047857; background: #d1fae5; }
.trend-red     { color: #b91c1c; background: #fee2e2; }
.trend-purple  { color: #6d28d9; background: #ede9fe; }
.trend-amber   { color: #92400e; background: #fef3c7; }

.kpi-card.dark .kpi-trend.trend-green {
  color: #34d399;
  background: rgba(16, 185, 129, 0.18);
}

/* =========================================================
   CHARTS
   ========================================================= */
.chart-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chart-header-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.chart-header-icon.bg-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.chart-title {
  font-size: 20px;
  font-weight: 600;
}

.chart-view-buttons {
  display: flex;
  gap: 6px;
}

/* =========================================================
   INSIGHT CARDS
   ========================================================= */
.insight-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.insight-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.bg-success-light {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.bg-warning-light {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.insight-list {
  max-height: 340px;
  overflow-y: auto;
}

.insight-item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.insight-item:hover {
  background: #f8fafc;
}

.insight-rank, .insight-rank-warning {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 16px;
  flex-shrink: 0;
  font-size: 18px;
}

.insight-rank {
  background: #f1f5f9;
  color: #64748b;
}

.rank-gold {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
}

.rank-silver {
  background: linear-gradient(135deg, #94a3b8, #64748b);
  color: white;
}

.rank-bronze {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: white;
}

.insight-rank-warning {
  font-size: 16px;
  font-weight: bold;
}

.high-risk {
  background: #fee2e2;
  color: #ef4444;
}

.medium-risk {
  background: #fed7aa;
  color: #f59e0b;
}

.low-risk {
  background: #d1fae5;
  color: #10b981;
}

.insight-content {
  flex: 1;
}

.insight-location {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.insight-meta {
  font-size: 14px;
}

.net-revenue.positive, .positive-value {
  color: #10b981;
  font-weight: 700;
  font-size: 18px;
}

.refund-amount.negative, .negative-value {
  color: #ef4444;
  font-weight: 700;
  font-size: 18px;
}

/* =========================================================
   DATA TABLES
   ========================================================= */
.data-table-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
}

.table-badge {
  font-size: 14px;
  padding: 6px 12px;
}

.table-wrapper {
  overflow-x: auto;
  max-height: 400px;
}

.table-wrapper-sm {
  overflow-x: auto;
}

.compact-table, .sales-table {
  width: 100%;
  font-size: 16px;
  border-collapse: collapse;
}

.compact-table th, .compact-table td,
.sales-table th, .sales-table td {
  padding: 14px 16px;
  border: 1px solid #dee2e6;
  font-size: 16px;
}

.compact-table th, .sales-table th {
  background: #c3cdd3;
  font-weight: 700;
  position: sticky;
  top: 0;
  white-space: nowrap;
  cursor: pointer;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.sales-table td { white-space: nowrap; }

.compact-table tbody tr, .sales-table tbody tr {
  cursor: pointer;
  transition: background 0.2s;
}

.compact-table tbody tr:hover, .sales-table tbody tr:hover {
  background: #e0e7ff !important;
}

.highlight-row {
  background: #dbeafe !important;
  border-left: 5px solid #6366f1;
}

.sale-amount {
  font-weight: 500;
}

.refund-amount {
  color: #ef4444;
}

.refund-badge {
  font-size: 14px;
  padding: 4px 10px;
}

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Scrollbar Styling */
.insight-list::-webkit-scrollbar,
.table-wrapper::-webkit-scrollbar,
.table-wrapper-sm::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.insight-list::-webkit-scrollbar-track,
.table-wrapper::-webkit-scrollbar-track,
.table-wrapper-sm::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.insight-list::-webkit-scrollbar-thumb,
.table-wrapper::-webkit-scrollbar-thumb,
.table-wrapper-sm::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.insight-list::-webkit-scrollbar-thumb:hover,
.table-wrapper::-webkit-scrollbar-thumb:hover,
.table-wrapper-sm::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* =========================================================
   RESPONSIVE
   ========================================================= */
@media (max-width: 1400px) {
  .kpi-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 992px) {
  .dashboard-container { padding: 16px; }
  .header-title { font-size: 24px; }
  .kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .kpi-value { font-size: 36px; }
  .kpi-icon-wrap { width: 64px; height: 64px; font-size: 28px; margin-bottom: 16px; }
  .kpi-card { padding: 28px 18px 22px; }
  .compact-table th, .compact-table td,
  .sales-table th, .sales-table td { padding: 10px 12px; font-size: 14px; }
  .chart-title { font-size: 16px; }
  .table-title { font-size: 16px; }
}

@media (max-width: 576px) {
  .dashboard-container { padding: 10px; }
  .kpi-grid { grid-template-columns: 1fr; }
  .kpi-value { font-size: 32px; }
  .kpi-label { font-size: 15px; }
  .kpi-trend { font-size: 13px; padding: 6px 12px; }
  .compact-table th, .compact-table td,
  .sales-table th, .sales-table td { padding: 8px 10px; font-size: 12px; }
  .dashboard-header h5 { font-size: 18px; }
  .header-subtitle { font-size: 12px; }
  .insight-location { font-size: 15px; }
  .insight-meta { font-size: 12px; }
  .net-revenue.positive, .positive-value,
  .refund-amount.negative, .negative-value { font-size: 15px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsRefunds.vue